import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "content"]
  
  connect() {
  }
  
  open(event) {
    
    event.preventDefault()
    event.stopPropagation() // 企業カードのクリックイベントを停止
    
    const recruitElement = event.currentTarget
    const recruitId = recruitElement.dataset.recruitId
    
    if (!recruitId) {
      console.error("❌ Recruit ID が見つかりません")
      return
    }
    
    // 企業名をデータ属性から取得
    this.companyName = recruitElement.dataset.companyName || ''
    this.companyId = recruitElement.dataset.companyId || ''
    
    
    // モーダルを先に表示してからコンテンツを読み込む
    this.showModal()
    this.loadRecruitDetail(recruitId)
  }
  
  close() {
    this.hideModal()
  }
  
  loadRecruitDetail(recruitId) {
    // ローディング状態を表示
    this.contentTarget.innerHTML = `
      <div class="flex items-center justify-center py-12">
        <div class="text-gray-400">
          <i class="fas fa-spinner fa-spin text-2xl mr-2"></i>
          <span>求人詳細を読み込み中...</span>
        </div>
      </div>
    `
    
    // 求人詳細情報をロードしてモーダル内容を更新
    fetch(`/companies_search/recruit/${recruitId}`)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        return response.text()
      })
      .then(html => {
        // サーバーから返されたHTMLをそのまま表示
        this.contentTarget.innerHTML = html
      })
      .catch(error => {
        console.error('求人詳細の取得に失敗しました:', error)
        this.contentTarget.innerHTML = `
          <div class="text-center py-12">
            <div class="text-red-500 mb-4">
              <i class="fas fa-exclamation-triangle text-4xl"></i>
            </div>
            <h3 class="text-lg font-semibold text-gray-900 mb-2">求人詳細の読み込みに失敗しました</h3>
            <p class="text-gray-600">しばらく時間をおいて再度お試しください。</p>
          </div>
        `
      })
  }
  
  extractRecruitInfo(html) {
    // HTMLから求人詳細情報を抽出する
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    
    // 会社名を取得（データ属性を優先）
    let companyName = this.companyName || '会社名未設定'
    
    // データ属性から会社名が取得できなかった場合のみ、HTMLから抽出
    if (!this.companyName || this.companyName.trim() === '') {
      // 1. HTMLの要素から取得
      const companyElement = doc.querySelector('.company-name, .company, h1, .recruit-title, .title')
      if (companyElement && companyElement.textContent.trim()) {
        companyName = companyElement.textContent.trim()
      } else {
        // 2. テキストから会社名を抽出（行の最初に出現する法人格）
        const textContent = doc.body.textContent || ''
        // 行の始まりまたは改行後に法人格が来るパターンを検索
        const companyMatch = textContent.match(/(?:^|\n)(株式会社|有限会社|合同会社|一般社団法人|公益財団法人|医療法人|学校法人|社会福祉法人|特定非営利活動法人|NPO法人)[\s　]*([^\s　\n最終更新日PDF採用対象時期職種募集必要勤務仕事給与昇給待遇休日退職通勤選考問い合わせ]+)/i)
        if (companyMatch) {
          companyName = (companyMatch[1] + companyMatch[2]).trim()
        } else {
          // 3. より緩い検索（カタカナの会社名も対応）
          const katakanaMatch = textContent.match(/(?:^|\n)(株式会社|有限会社|合同会社)[\s　]*([ア-ヶー\w]+)/i)
          if (katakanaMatch) {
            companyName = (katakanaMatch[1] + katakanaMatch[2]).trim()
          } else {
            // 4. 問い合わせ先から会社名を抽出
            const contactMatch = textContent.match(/問い合わせ先[\s\S]*?(株式会社|有限会社|合同会社)[\s　]*([^\s　\n]+)/i)
            if (contactMatch) {
              companyName = (contactMatch[1] + contactMatch[2]).trim()
            }
          }
        }
      }
    }
    
    // メインコンテンツを取得
    const mainContent = doc.querySelector('main, .main-content, .recruit-detail, .content') || doc.body
    
    // 不要な要素（ナビゲーション、フッターなど）を除外
    const excludeSelectors = ['nav', 'header', 'footer', '.navigation', '.sidebar', 'script', 'style']
    excludeSelectors.forEach(selector => {
      const elements = mainContent.querySelectorAll(selector)
      elements.forEach(el => el.remove())
    })
    
    // テーブル形式で求人情報を整理
    const recruitInfo = this.parseRecruitData(mainContent)
    
    return `
      <div class="space-y-6 p-4">
        <div class="border-b pb-4">
          <h2 class="text-xl font-bold text-gray-900 mb-2">${companyName}</h2>
        </div>
        <div class="overflow-x-auto px-4">
          <table class="w-full border-collapse">
            <tbody>
              ${this.generateTableRows(recruitInfo)}
            </tbody>
          </table>
        </div>
        <div class="flex justify-center pt-6 border-t">
          <button class="bg-orange-500 text-white px-6 py-2 rounded-full hover:bg-orange-600 transition-colors">
            <i class="fas fa-file-pdf mr-2"></i>
            PDF出力
          </button>
        </div>
      </div>
    `
  }
  
  parseRecruitData(content) {
    // コンテンツから求人情報を抽出してテーブル用データに変換
    const data = []
    const textContent = content.textContent.trim()
    
    // テキストから特定の項目を抽出するパターン（より細かく分割）
    const patterns = [
      { key: '採用対象', pattern: /採用対象([^\n]*?)(?=採用時期|時期|期間|職種|勤務|給与|$)/i },
      { key: '採用時期または期間', pattern: /(?:採用時期または期間|採用時期|時期または期間|期間)([^\n]*?)(?=職種|勤務|給与|募集|$)/i },
      { key: '職種分類', pattern: /職種分類([^\n]*?)(?=募集|必要|勤務|給与|$)/i },
      { key: '募集人数', pattern: /募集人数([^\n]*?)(?=必要|勤務|給与|資格|$)/i },
      { key: '必要資格など', pattern: /(?:必要資格など|必要資格|資格)([^\n]*?)(?=勤務|給与|転勤|$)/i },
      { key: '勤務地', pattern: /勤務地(?!\（)([^\n]*?)(?=転勤|勤務地\（|仕事|給与|$)/i },
      { key: '勤務地（詳細）', pattern: /勤務地\（詳細\）([^\n]*?)(?=仕事|給与|勤務時間|$)/i },
      { key: '仕事の内容', pattern: /仕事の内容([\s\S]*?)(?=勤務時間|給与|昇給|$)/i },
      { key: '勤務時間', pattern: /勤務時間([^\n]*?)(?=給与|昇給|待遇|$)/i },
      { key: '給与', pattern: /給与([^\n]*?)(?=昇給|待遇|賞与|$)/i },
      { key: '昇給・賞与', pattern: /昇給・賞与([\s\S]*?)(?=待遇|福利厚生|休日|$)/i },
      { key: '待遇・福利厚生', pattern: /待遇・福利厚生([\s\S]*?)(?=休日|退職金|$)/i },
      { key: '休日・休暇', pattern: /休日・休暇([\s\S]*?)(?=退職金|通勤|選考|$)/i },
      { key: '退職金', pattern: /退職金([^\n]*?)(?=通勤|選考|その他|$)/i },
      { key: '通勤手当', pattern: /通勤手当([^\n]*?)(?=選考|その他|問い合わせ|$)/i },
      { key: '選考プロセス', pattern: /選考プロセス([\s\S]*?)(?=その他|問い合わせ|$)/i },
      { key: 'マイカー通勤', pattern: /\*マイカー通勤([^\n]*?)(?=\*|問い合わせ|$)/i },
      { key: '試用期間', pattern: /\*試用期間([^\n]*?)(?=\*|問い合わせ|$)/i },
      { key: '文理・専攻', pattern: /\*文理、専攻([^\n]*?)(?=\*|問い合わせ|$)/i },
      { key: '問い合わせ先', pattern: /問い合わせ先([\s\S]*?)$/i }
    ]
    
    patterns.forEach(item => {
      const match = textContent.match(item.pattern)
      if (match && match[1]) {
        const value = match[1].trim()
        if (value && value.length > 0) {
          // 改行を適切に処理し、不要な空白を削除
          const cleanValue = value
            .replace(/\s+/g, ' ')
            .replace(/\n+/g, '\n')
            .trim()
          
          data.push({ 
            label: item.key, 
            value: cleanValue
          })
        }
      }
    })
    
    // 基本情報が見つからない場合のフォールバック
    if (data.length === 0) {
      const fallbackItems = [
        { label: '採用対象', value: '詳細は会社にお問い合わせください' },
        { label: '採用時期または期間', value: '随時募集' },
        { label: '職種分類', value: '情報を確認中...' },
        { label: '詳細情報', value: textContent || '情報を読み込み中...' }
      ]
      data.push(...fallbackItems)
    }
    
    return data
  }
  
  generateTableRows(data) {
    return data.map(item => {
      // 長いテキストの場合は改行を<br>に変換
      const formattedValue = item.value
        .replace(/\n/g, '<br>')
        .replace(/【([^】]+)】/g, '<strong>【$1】</strong>') // 【】で囲まれた部分を太字に
        .replace(/(?:\*|・)([^\n]+)/g, '• $1') // リストアイテムを整形
      
      return `
        <tr class="border-b border-white">
          <td class="bg-gray-100 font-bold text-gray-900 p-2 md:p-3 w-1/5 md:w-1/4 align-top text-xs md:text-sm leading-tight">${item.label}</td>
          <td class="bg-white text-gray-800 p-2 md:p-3 align-top leading-relaxed text-xs md:text-sm">${formattedValue}</td>
        </tr>
      `
    }).join('')
  }
  
  showModal() {
    if (this.modalTarget) {
      this.modalTarget.classList.remove('hidden')
      document.body.style.overflow = 'hidden'
    } else {
      console.error("❌ Modal target not found!")
    }
  }
  
  hideModal() {
    this.modalTarget.classList.add('hidden')
    document.body.style.overflow = ''
  }
  
  // ESCキーでモーダルを閉じる
  keydown(event) {
    if (event.key === 'Escape') {
      this.close()
    }
  }
}
